import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { notificacionesAPI } from '../../services/api';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import { Bell, User, LogOut, Settings, Menu, X, BellRing } from 'lucide-react';
import { translations } from '../../utils/translations';
import PushNotificationButton from '../PushNotificationButton';

// Brand logos - Nueva imagen C&M 2024 (favicon circular)
const LOGO_ICON = 'https://customer-assets.emergentagent.com/job_5612798b-7eab-4f57-bce1-b7ba348a317b/artifacts/j94l7bv6_favicon_3-removebg-preview%281%29.png';

const Navbar = () => {
  const { user, logout, isAdmin, isTallerUser } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifCount, setNotifCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);

  // Textos traducidos
  const txt = translations.navbar;

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (user) {
      loadNotifCount();
      const interval = setInterval(loadNotifCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const loadNotifCount = async () => {
    try {
      const response = await notificacionesAPI.getCount();
      setNotifCount(response.data.count);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const handleLogout = useCallback(() => {
    setMobileMenuOpen(false);
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const handleMobileNavClick = useCallback(() => {
    // Small delay to allow the navigation to complete before closing menu
    setTimeout(() => {
      setMobileMenuOpen(false);
    }, 100);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
  }, []);

  return (
    <nav className="bg-gradient-to-r from-slate-900 via-slate-900 to-teal-900 text-white shadow-xl sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3 group">
              <img 
                src={LOGO_ICON} 
                alt="C&M" 
                className="h-10 w-10 object-contain group-hover:scale-105 transition-transform drop-shadow-lg"
              />
              <span className="font-bold text-lg hidden sm:block">
                <span className="text-white">C&M</span>
                <span className="text-teal-400 ml-1 text-sm">Servicios Aeronáuticos</span>
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Link 
              to="/" 
              className="px-3 py-2 rounded-lg hover:bg-white/10 hover:text-teal-400 transition-colors text-sm font-medium"
            >
              {t(txt.inicio)}
            </Link>
            <Link 
              to="/talleres" 
              className="px-3 py-2 rounded-lg hover:bg-white/10 hover:text-teal-400 transition-colors text-sm font-medium"
            >
              {t(txt.talleres)}
            </Link>
            <Link 
              to="/capacitaciones" 
              className="px-3 py-2 rounded-lg hover:bg-white/10 hover:text-teal-400 transition-colors text-sm font-medium"
            >
              {t(txt.capacitaciones)}
            </Link>
            <Link 
              to="/gestoria" 
              className="px-3 py-2 rounded-lg hover:bg-white/10 hover:text-teal-400 transition-colors text-sm font-medium"
              data-testid="nav-gestoria"
            >
              Gestoría
            </Link>
            
            {user ? (
              <>
                {isAdmin() && (
                  <Link to="/admin" className="px-3 py-2">
                    <Badge className="bg-orange-500 hover:bg-orange-600 text-white">Admin</Badge>
                  </Link>
                )}
                
                {isTallerUser() && (
                  <Link 
                    to="/panel" 
                    className="px-3 py-2 rounded-lg hover:bg-white/10 hover:text-teal-400 transition-colors text-sm font-medium"
                  >
                    {t(txt.miPanel)}
                  </Link>
                )}

                <Link to="/notificaciones" className="relative px-3 py-2">
                  <Bell className="h-5 w-5 hover:text-teal-400 transition-colors" />
                  {notifCount > 0 && (
                    <span className="absolute top-1 right-1 bg-orange-500 text-xs rounded-full h-4 w-4 flex items-center justify-center font-bold">
                      {notifCount > 9 ? '9+' : notifCount}
                    </span>
                  )}
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-white hover:text-teal-400 hover:bg-white/10">
                      <User className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user.full_name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <div className="px-2 py-1.5">
                      <PushNotificationButton variant="hidden-when-active" showLabel={true} className="w-full justify-start" />
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/perfil')}>
                      <Settings className="mr-2 h-4 w-4" />
                      {t(txt.configuracion)}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                      <LogOut className="mr-2 h-4 w-4" />
                      {t(txt.cerrarSesion)}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center space-x-2 ml-2">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')} 
                  className="text-white hover:text-teal-400 hover:bg-white/10"
                >
                  {t(txt.iniciarSesion)}
                </Button>
                <Button 
                  onClick={() => navigate('/suscripcion')} 
                  className="bg-teal-500 hover:bg-teal-600 text-white shadow-lg shadow-teal-500/25"
                >
                  {t(txt.suscribirse)}
                </Button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button 
              ref={menuButtonRef}
              type="button"
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-lg text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-teal-400 transition-colors"
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
            >
              <span className="sr-only">Abrir menú principal</span>
              {mobileMenuOpen ? (
                <X className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-[998]"
          style={{ top: '64px' }}
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div 
          ref={menuRef}
          id="mobile-menu"
          className="md:hidden fixed left-0 right-0 z-[999] bg-slate-900 border-t border-white/10 shadow-xl"
          style={{ top: '64px', maxHeight: 'calc(100vh - 64px)', overflowY: 'auto' }}
        >
        <div className="px-4 py-4 space-y-1">
          <Link 
            to="/" 
            className="block py-3 px-4 rounded-lg hover:bg-white/10 hover:text-teal-400 transition-colors font-medium" 
            onClick={handleMobileNavClick}
          >
            {t(txt.inicio)}
          </Link>
          <Link 
            to="/talleres" 
            className="block py-3 px-4 rounded-lg hover:bg-white/10 hover:text-teal-400 transition-colors font-medium" 
            onClick={handleMobileNavClick}
          >
            {t(txt.talleres)}
          </Link>
          <Link 
            to="/capacitaciones" 
            className="block py-3 px-4 rounded-lg hover:bg-white/10 hover:text-teal-400 transition-colors font-medium" 
            onClick={handleMobileNavClick}
          >
            {t(txt.capacitaciones)}
          </Link>
          <Link 
            to="/gestoria" 
            className="block py-3 px-4 rounded-lg hover:bg-white/10 hover:text-violet-300 transition-colors font-medium" 
            onClick={handleMobileNavClick}
            data-testid="nav-gestoria-mobile"
          >
            Gestoría
          </Link>
          
          {user ? (
            <>
              <div className="border-t border-white/10 my-3" />
              
              {isAdmin() && (
                <Link 
                  to="/admin" 
                  className="block py-3 px-4 rounded-lg hover:bg-white/10" 
                  onClick={handleMobileNavClick}
                >
                  <Badge className="bg-orange-500 text-white">Admin Panel</Badge>
                </Link>
              )}
              
              {isTallerUser() && (
                <Link 
                  to="/panel" 
                  className="block py-3 px-4 rounded-lg hover:bg-white/10 hover:text-teal-400 transition-colors font-medium" 
                  onClick={handleMobileNavClick}
                >
                  {t(txt.miPanel)}
                </Link>
              )}
              
              <Link 
                to="/notificaciones" 
                className="flex items-center justify-between py-3 px-4 rounded-lg hover:bg-white/10 hover:text-teal-400 transition-colors font-medium" 
                onClick={handleMobileNavClick}
              >
                <span className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  {t(txt.notificaciones)}
                </span>
                {notifCount > 0 && (
                  <Badge className="bg-orange-500">{notifCount > 9 ? '9+' : notifCount}</Badge>
                )}
              </Link>
              
              <div className="border-t border-white/10 my-3" />
              
              <div className="px-4 py-2">
                <p className="text-sm font-medium">{user.full_name}</p>
                <p className="text-xs text-gray-400">{user.email}</p>
              </div>
              
              <Link 
                to="/perfil" 
                className="flex items-center gap-2 py-3 px-4 rounded-lg hover:bg-white/10 hover:text-teal-400 transition-colors font-medium" 
                onClick={handleMobileNavClick}
              >
                <Settings className="h-5 w-5" />
                {t(txt.configuracion)}
              </Link>
              
              <div className="px-4 py-2">
                <PushNotificationButton variant="hidden-when-active" showLabel={true} className="w-full justify-start text-white hover:text-teal-400" />
              </div>
              
              <button 
                type="button"
                onClick={handleLogout} 
                className="flex items-center gap-2 py-3 px-4 text-red-400 hover:text-red-300 hover:bg-white/10 rounded-lg w-full text-left transition-colors font-medium"
              >
                <LogOut className="h-5 w-5" />
                {t(txt.cerrarSesion)}
              </button>
            </>
          ) : (
            <>
              <div className="border-t border-white/10 my-3" />
              <Link 
                to="/login" 
                className="block py-3 px-4 rounded-lg hover:bg-white/10 hover:text-teal-400 transition-colors font-medium" 
                onClick={handleMobileNavClick}
              >
                {t(txt.iniciarSesion)}
              </Link>
              <Link 
                to="/suscripcion" 
                className="block py-3 px-4 bg-teal-500 rounded-lg text-center font-medium hover:bg-teal-600 transition-colors mt-2" 
                onClick={handleMobileNavClick}
              >
                {t(txt.suscribirse)}
              </Link>
            </>
          )}
        </div>
      </div>
      )}
    </nav>
  );
};

export default Navbar;
